import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { Modal } from "@/components/modal";
import EkoElement from "@/utilities/element";
import "@/components/icon";
import "@/components/tooltip";

@customElement("eko-page-header-help-modal")
class PageHeaderHelpModal extends EkoElement {
  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.hide();
    }
  };

  private modalRef: Ref<Modal> = createRef();

  // Restore focus to the element that was focused before the modal opened
  private restoreFocus = async () => {
    await this.updateComplete;
    this.restoreFocusElement?.value?.focus({ preventScroll: true });
    this.restoreFocusElement = null;
  };

  private restoreFocusElement?: Ref<HTMLElement> | null;

  @property({ reflect: true, type: String }) heading = "Help";
  @property({ reflect: true, type: Boolean }) resources = false;

  async hide() {
    this.modalRef.value?.hide();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.restoreFocus();
  }

  render() {
    return html`<eko-modal variant="info" ${ref(this.modalRef)}>
      <eko-modal-title slot="title">${this.heading}</eko-modal-title>
      <eko-stack>
        <slot></slot>
        ${this.resources
          ? html`
              <eko-card padding="md">
                <eko-card-header slot="header" padding="lg">Resources</eko-card-header>
                <eko-action-group>
                  <slot name="resources"></slot>
                </eko-action-group>
              </eko-card>
            `
          : nothing}
      </eko-stack>
    </eko-modal>`;
  }

  async show({ restoreFocusElement }: { restoreFocusElement: Ref<HTMLElement> | null }) {
    if (!this.modalRef.value) return;

    this.restoreFocusElement = restoreFocusElement;
    document.addEventListener("keydown", this.handleKeyDown);
    this.modalRef.value.show();

    await this.updateComplete;
    this.modalRef.value?.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-page-header-help-modal": PageHeaderHelpModal;
  }
}

export default PageHeaderHelpModal;
